import { defineStore, storeToRefs } from 'pinia'
import { collection, query, where } from '@firebase/firestore'
import { useOrganizationsStore } from '~/store/organizations'
import type { Workspace } from '~/types/workspaces'
import { Collections } from '~/constants/firebase'
import { WorkspaceRoles, WorkspaceVisibility } from '~/constants/workspaces'

export const useWorkspacesStore = defineStore('workspacesStore', () => {
  const workspaces = ref<Workspace[]>([])

  async function getWorkspaces() {
    const userUid = (await getCurrentUser()).uid
    await new Promise((resolve) => {
      const organizationsStore = useOrganizationsStore()
      const { selectedOrganization } = storeToRefs(organizationsStore)

      if (!selectedOrganization.value) {
        throw new Error(
          'Must have to select an organization before fetching related workspaces!',
        )
      }

      const path = Collections.Workspaces.base(selectedOrganization.value.id)
      const { promise: publicWorkspacesPromise } = useCollection(
        query(
          collection(useGetFirestore(), path),
          where('visibility', '==', WorkspaceVisibility.Public),
        ),
        {
          wait: true,
          // Due to issue => https://github.com/vuejs/vuefire/issues/1315
          ssrKey: path,
        },
      )
      const { promise: userWorkspacesPromise } = useCollection(
        collection(
          useGetFirestore(),
          Collections.Users.Organizations.workspaces(userUid, selectedOrganization.value.id),
        ),
        { wait: true },
      )

      // TODO: Check to watchEffect to not being called several times
      Promise.all([
        publicWorkspacesPromise.value,
        userWorkspacesPromise.value,
      ]).then(([publicWorkspaces, userWorkspaces]) => {
        watchEffect(() => {
          setWorkspaces([
            ...userWorkspaces,
            ...publicWorkspaces
              .filter(
                publicWorkspace =>
                  !userWorkspaces
                    .map(workspace => workspace.workspace.id)
                    .includes(publicWorkspace.id),
              )
              .map(publicWorkspace => ({
                role: WorkspaceRoles.User,
                workspace: publicWorkspace,
              })),
          ])
        })
        resolve()
      })
    })
  }
  function setWorkspaces(value: Workspace[]) {
    workspaces.value = value
  }

  const selectedWorkspace = ref<Workspace | null>(null)
  function setSelectedWorkspace(value: Workspace | null) {
    const { $sentrySetTag } = useNuxtApp()

    selectedWorkspace.value = value
    $sentrySetTag('workspaceId', value?.id)
  }

  function $reset() {
    workspaces.value = []
    selectedWorkspace.value = null
  }

  return {
    workspaces,
    getWorkspaces,
    setWorkspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    $reset,
  }
})
